import { useTheme } from "@material-ui/core";
import { transparentize } from "polished";
import { GroupBase, StylesConfig, ThemeConfig } from "react-select5";
import { Color } from "../../../../theme/color";

type ExternalProps = {
  menu?: {
    showBorder?: boolean;
  } & React.CSSProperties;
  control?: {
    showBorder?: boolean;
  } & React.CSSProperties;
  menuPortal?: React.CSSProperties;
  menuList?: React.CSSProperties;
  placeholder?: React.CSSProperties;
  container?: React.CSSProperties;
  option?: React.CSSProperties;
  multiValue?: React.CSSProperties;
  multiValueLabel?: React.CSSProperties;
  groupHeading?: React.CSSProperties;
  indicatorsContainer?: React.CSSProperties;
  valueContainer?: React.CSSProperties;
};

type InternalProps = {
  grouped: boolean;
};

export const useCreateableSelectStyle = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  internalProps: InternalProps,
  externalProps?: ExternalProps
) => {
  const theme = useTheme();
  const creatableSelectStyle: StylesConfig<Option, IsMulti, Group> = {
    container: (base) => ({
      ...base,
      fontFamily: theme.typography.fontFamily,
      ...(externalProps?.container || {})
    }),
    menuPortal: (base) => ({
      ...base,
      fontFamily: theme.typography.fontFamily,
      ...(externalProps?.menuPortal || {})
    }),
    placeholder: (baseStyles) => ({
      ...baseStyles,
      ...(theme.typography.body1 as any),
      color: Color.DetailsInput,
      ...(externalProps?.placeholder || {})
    }),
    option: (baseStyles, state) => ({
      ...baseStyles,
      fontSize: theme.typography.body2.fontSize,
      backgroundColor: state.isFocused
        ? `${transparentize(0.85, theme.palette.primary.main)}`
        : undefined,
      ...(externalProps?.option || {})
    }),
    groupHeading: (baseStyles) => ({
      ...baseStyles,
      color: theme.palette.info.main,
      textTransform: "none",
      ...(externalProps?.groupHeading || {})
    }),
    multiValue: (baseStyles) => ({
      ...baseStyles,
      backgroundColor: transparentize(0.75, Color.DoveBlue),
      border: `0.5px solid ${Color.DoveBlue}`,
      padding: "0 0.25rem",
      fontSize: theme.typography.subtitle1.fontSize,
      fontStyle: theme.typography.subtitle1.fontStyle,
      fontWeight: theme.typography.subtitle1.fontWeight,
      lineHeight: theme.typography.subtitle1.lineHeight,
      ...(externalProps?.multiValue || {})
    }),
    multiValueLabel: (baseStyles) => ({
      ...baseStyles,
      fontSize: theme.typography.subtitle1.fontSize,
      fontStyle: theme.typography.subtitle1.fontStyle,
      fontWeight: theme.typography.subtitle1.fontWeight,
      lineHeight: theme.typography.subtitle1.lineHeight,
      ...(externalProps?.multiValueLabel || {})
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      backgroundColor: Color.White,
      border: externalProps?.menu?.showBorder ? baseStyles.border : "none",
      boxShadow: externalProps?.menu?.showBorder ? baseStyles.boxShadow : "none",
      ...(externalProps?.menu || {})
    }),
    menuList: (baseStyles) => ({
      ...baseStyles,
      // Hide last divider
      "> :last-child ": {
        display: internalProps.grouped ? "none" : undefined
      },
      ...(externalProps?.menuList || {})
    }),
    control: (baseStyles) => ({
      ...baseStyles,
      minHeight: "2.5rem",
      border: externalProps?.control?.showBorder ? baseStyles.border : "none",
      boxShadow: externalProps?.control?.showBorder ? baseStyles.boxShadow : "none",
      "&:hover": {
        ...baseStyles[":hover"],
        cursor: "pointer"
      },
      ...(externalProps?.control || {})
    }),
    indicatorsContainer: (base) => ({
      ...base,
      paddingRight: "0.5rem",
      ...(externalProps?.indicatorsContainer || {})
    }),
    valueContainer: (base) => ({
      ...base,
      ...(externalProps?.valueContainer || {})
    })
  };

  const creatableSelectTheme: Partial<ThemeConfig> = {
    colors: {
      primary: theme.palette.primary.main,
      neutral20: theme.palette.info.light
    }
  };
  return { creatableSelectStyle, creatableSelectTheme };
};
